import React from 'react';
import { TGender, TProfilesFilters } from '../../types';
import { useAppSelector } from '../../services/store/hooks';
import { useDebounce } from '../../hooks/useDebounce';
import styles from './profiles-filter.module.css';
import AgeFilter from './age-filter';
import { useScrollToTopOnOpen } from '../../hooks/useScrollToTopOnOpen';
import { useGetProfilesCountQuery } from '../../services/store/api/profiles';

interface ProfilesFilterProps {
  value: TProfilesFilters;
  onChange: (filters: TProfilesFilters) => void;
  onApply: () => void;
  onReset: () => void;
  isOpen: boolean;
}

interface TFilterGender {
  type: TGender;
  label: string;
}

const ProfilesFilter: React.FC<ProfilesFilterProps> = ({
  value,
  onChange,
  onApply,
  onReset,
  isOpen,
}) => {
  const { goals, categories } = useAppSelector((state) => state.dict);
  const scrollRef = React.useRef<HTMLDivElement>(null);
  
  const genders: TFilterGender[] = [
    { type: 'male', label: 'Мужской' },
    { type: 'female', label: 'Женский' },
  ];

  // 👇 Debounce фильтров для подсчёта (300ms задержка)
  const debouncedFilters = useDebounce(value, 300);

  // 👇 RTK Query: автоматическое кэширование, загрузка, отмена запросов
  const {
    data: countData,
    isLoading: isCountLoading,
    isFetching: isCountRefetching,
  } = useGetProfilesCountQuery(
    isOpen ? debouncedFilters : ({} as TProfilesFilters),
    { skip: !isOpen } // не делаем запрос, если модалка закрыта
  );

  const profilesCount = countData?.count ?? null;
  const showLoading = isCountLoading || isCountRefetching;

  const toggleArrayValue = (field: 'goals' | 'interests', item: number) => {
    onChange({
      ...value,
      [field]: value[field].includes(item)
        ? value[field].filter((i) => i !== item)
        : [...value[field], item],
    });
  };

  const isGenderActive = (gender?: TGender) =>
    value.gender === gender || (!gender && !value.gender);

  // ---------------- PREMIUM TABS LOGIC ----------------
  const [activeCategoryId, setActiveCategoryId] =
    React.useState<number | null>(null);

  const activeTabRef = React.useRef<HTMLButtonElement | null>(null);

  React.useEffect(() => {
    if (categories?.length && activeCategoryId === null) {
      setActiveCategoryId(categories[0].id);
    }
  }, [categories, activeCategoryId]);

  // автоскролл активного таба в центр
  React.useEffect(() => {
    if (activeTabRef.current) {
      activeTabRef.current.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }
  }, [activeCategoryId]);

  useScrollToTopOnOpen({
    trigger: isOpen,
    scrollRef,
    modalSelector: '[role="dialog"]',
    fallbackDelay: 150,
  });

  return (
    <div className={styles.wrapper}>
      <div className={styles.scroll} ref={scrollRef}>
        <div className={styles.content}>
          {/* Возраст */}
          <section>
            <AgeFilter
              value={value.age ?? null}
              onChange={(val) => onChange({ ...value, age: val })}
            />
          </section>

          {/* Пол */}
          <section>
            <div className={styles.sectionHeader}>
              <h3 className={styles.h3}>Пол</h3>
              {value.gender && (
                <button
                  type="button"
                  className={styles.sectionReset}
                  onClick={() => onChange({ ...value, gender: undefined })}
                >
                  Сбросить
                </button>
              )}
            </div>
            <div className={styles.buttonsRow}>
              <button
                type="button"
                className={
                  isGenderActive(undefined)
                    ? styles.interestActive
                    : styles.interestButton
                }
                onClick={() => onChange({ ...value, gender: undefined })}
              >
                Любой
              </button>

              {genders.map((gender) => (
                <button
                  key={gender.type}
                  type="button"
                  className={
                    isGenderActive(gender.type)
                      ? styles.interestActive
                      : styles.interestButton
                  }
                  onClick={() => onChange({ ...value, gender: gender.type })}
                >
                  {gender.label}
                </button>
              ))}
            </div>
          </section>

          {/* Цели */}
          <section>
            <div className={styles.sectionHeader}>
              <h3 className={styles.h3}>Цели</h3>
              {value.goals.length > 0 && (
                <button
                  type="button"
                  className={styles.sectionReset}
                  onClick={() => onChange({ ...value, goals: [] })}
                >
                  Сбросить
                </button>
              )}
            </div>
            <div className={styles.buttonsRow}>
              {goals.map((goal) => {
                const isActive = value.goals.includes(goal.id);
                return (
                  <button
                    key={goal.id}
                    type="button"
                    className={
                      isActive ? styles.interestActive : styles.interestButton
                    }
                    onClick={() => toggleArrayValue('goals', goal.id)}
                  >
                    {goal.title}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Интересы */}
          <section>
            <div className={styles.sectionHeader}>
              <h3 className={styles.h3}>Интересы</h3>
              {value.interests.length > 0 && (
                <button
                  type="button"
                  className={styles.sectionReset}
                  onClick={() => onChange({ ...value, interests: [] })}
                >
                  Сбросить
                </button>
              )}
            </div>

            <div className={styles.premiumTabs}>
              {categories.map((category) => {
                const selectedCount = category.interests.filter((i) =>
                  value.interests.includes(i.id)
                ).length;

                const isActive = category.id === activeCategoryId;

                return (
                  <button
                    key={category.id}
                    ref={isActive ? activeTabRef : null}
                    type="button"
                    onClick={() => setActiveCategoryId(category.id)}
                    className={`${styles.premiumTab} ${
                      isActive ? styles.premiumTabActive : ''
                    }`}
                  >
                    {category.title}
                    {selectedCount > 0 && (
                      <span className={styles.premiumCounter}>
                        {selectedCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {categories
              .filter((c) => c.id === activeCategoryId)
              .map((category) => (
                <div key={category.id} className={styles.premiumContent}>
                  <div className={styles.buttonsRow}>
                    {category.interests.map((interest) => {
                      const isActive = value.interests.includes(interest.id);

                      return (
                        <button
                          key={interest.id}
                          type="button"
                          className={
                            isActive
                              ? styles.interestActive
                              : styles.interestButton
                          }
                          onClick={() =>
                            toggleArrayValue('interests', interest.id)
                          }
                        >
                          {interest.title}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
          </section>
        </div>
      </div>

      {/* 👇 Actions с кнопкой и счётчиком */}
      <div className={styles.actions}>
        <button
          type="button"
          className={styles.apply}
          onClick={onApply}
          disabled={showLoading}
          title={
            profilesCount !== null
              ? `Найдено профилей: ${profilesCount}`
              : undefined
          }
        >
          <span>Применить</span>

          {/* Счётчик: показываем, если данные загружены */}
          {!showLoading && profilesCount !== null && (
            <span className={styles.applyCounter}>{profilesCount}</span>
          )}

          {/* Индикатор загрузки */}
          {showLoading && <span className={styles.applyLoading}>⋯</span>}
        </button>

        <button
          type="button"
          className={styles.reset}
          onClick={onReset}
          disabled={showLoading}
        >
          Сбросить
        </button>
      </div>
    </div>
  );
};

export default ProfilesFilter;